// Copyright 2021 Pangea Cyber Corporation
// Author: Pangea Cyber Corporation

/**
 * Check if the input URL is a qualified URL that can be parsed
 *
 * @param url Url string to test
 * @returns URL object if the URL is qualified and valid, otherwise return nothing
 */
export const isQualifiedUrl = (url: string) => {
  try {
    const urlRef = new URL(url);

    return urlRef;
  } catch (e) {
    return;
  }
};

/**
 * Takes a partial or qualified URL and appends query parameters to it
 * @param url A partial or fully qualified URL.
 * @param parameters An object of string keys and values to append to the URL
 * @returns a string representation of the final URL
 */
export const addParameters = (
  url: string,
  parameters: { [key: string]: string } = {},
) => {
  try {
    const qualifiedUrl = isQualifiedUrl(url);

    if (qualifiedUrl) {
      for (const key of Object.keys(parameters)) {
        qualifiedUrl.searchParams.set(key, parameters[key]);
      }

      return qualifiedUrl.toString();
    } else {
      const urlParts = url.split("?");
      const parametersString = urlParts[1] ?? "";

      const searchParams = new URLSearchParams(parametersString);

      for (const key of Object.keys(parameters)) {
        searchParams.set(key, parameters[key]);
      }

      return `${urlParts[0]}${
        searchParams.size > 0 ? "?" : ""
      }${searchParams.toString()}`;
    }
  } catch (_e) {
    // Ignore the error, always return the URL input so we
    // dont break any pages
  }

  return url;
};

// add mlp=true to show left panel on authn login/signup
export const consoleURL = `${
  process.env.GATSBY_PUC_UI_URL || "https://console.pangea.cloud"
}?pm=mlp`;

export const aiChallengeURL = "https://escape.pangea.cloud?referrer=www";

export const docsURL = `${process.env.GATSBY_DOCS_BASE_URL}/docs`;

export const consoleLoginURL = addParameters(consoleURL, {
  referrer: "www",
});

export const consoleSignupURL = addParameters(consoleURL, {
  referrer: "www",
});

export const consoleSignupURLWithGoogle = addParameters(consoleSignupURL, {
  social: "google",
});
export const consoleSignupURLWithGithub = addParameters(consoleSignupURL, {
  social: "github",
});

export const termsOfUseURL = `/terms-of-use/`;
export const privacyPolicyURL = `https://www.crowdstrike.com/en-us/legal/privacy-notice/`;

export const contactUsURL = `/contact-us/`;
export const bookADemoURL = addParameters(contactUsURL, {
  exp: "demo",
});

export const bookAnAidrDemoURL = addParameters("/talk-to-an-expert/", {
  // notes: `I'd like access to the AIDR platform to secure my AI`,
});
export const enterprisePricingUrl = addParameters(contactUsURL, {
  notes: `I'd like to learn more about your enterprise pricing`,
});

export const addUrlParams = (url: string): string => {
  if (typeof window !== "undefined") {
    const queryString = window.location.search.slice(1);
    const separator = url.includes("?") ? "&" : "?";
    const result = queryString ? `${url}${separator}${queryString}` : url;

    return result;
  }

  return url;
};

export const sanitizeConfig = {
  allowedTags: [],
  allowedAttributes: {},
};
